import React, { Component } from 'react';
import { Route, Switch, withRouter } from 'react-router-dom';
import { getRoutes, protectedRoutes, routes } from '../../routes';
import './Base.css';
import Header from '../../components/header/Header';
import LeftMenu from '../../components/leftmenu/LeftMenu';
import IdleTimer from 'react-idle-timer';

// Redux
import withShipment from '../../withShipment';
import {
  userInfoSelector,
  isAuthenticatedSelector,
  employeesSelector,
} from '../../redux/selectors/auth';
import {
  authLogout,
  getEmployees,
  updateUserInfo,
} from '../../redux/actions/auth';
import { ROUTE_TYPE } from '../../routes/constants';
import Login from '../Login/Login';
import { showingMenuItemsSelector } from '../../redux/selectors/global';
import PropTypes from 'prop-types';
import Slide from '@material-ui/core/Slide';
import {
  setBlacklistAlert,
  triggerRefresh,
  listAvailableCompanies,
} from '../../redux/actions/settings';
import { processRFID, registerRFID } from '../../redux/actions/userManagement';
import ClockInOut from '../../components/clockinout/ClockInOut';
import { Redirect } from 'react-router';
import PageNotFound from '../../routes/PageNotFound';
import CheckIn404 from '../../routes/CheckIn404';
import { ROLE_ADMIN } from '../../components/global/constants';
import {
  cleanLayoutPaths,
  ROLE_MASTER_ADMIN,
  ROLE_TECH_ADMIN,
} from '../../components/settings/ManageUsers/constants';
import AuthUtil from '../../redux/AuthUtil';
import Footer from '../../components/footer/Footer';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Alert from '@material-ui/lab/Alert';
import { blacklistAlertSelector } from '../../redux/selectors/ticketOut';
import { Grid } from '@mui/material';
import { BASE_URL } from '../../components/global/Server/endpoints';

class Base extends Component {
  constructor(props) {
    super(props);
    const isIOSSafari = /iP(ad|hone|od).*Safari/i.test(navigator.userAgent);
    this.state = {
      isMobile: window.matchMedia('(max-width: 800px)').matches || isIOSSafari,
      socket: null,
      socketRfid: null,
      messages: [],
      rfid: '',
      triggerUpdate: false,
      announcementMessage: '',
      announcementEnd: '',
      serverStatus: '',
      showProfile: false,
      showSelfRegister: false,
      tempRfid: '', // To store the rfid while we choose a user
      selectedEmployeeId: '',
      feedbackOpen: false,
      feedbackMessage: '',
      feedbackSeverity: 'success', // 'success' | 'error'
      isSubmitting: false, // To show a loading spinner on the button

      //alert
      isImageZoomed: false,
    };

    // Refs for video and canvas
    this.videoRef = React.createRef();
    this.canvasRef = React.createRef();
    this.inputRef = React.createRef();
    this.buffer = '';
    this.timer = null;
    this.idleTimer = null;
    this.wakeLock = null;
  }

  componentDidMount() {
    this.mediaQuery = window.matchMedia('(max-width: 800px)');
    this.mediaQuery.addEventListener('change', this.handleScreenChange);
    this.requestWakeLock();

    this.initConnections();
  }

  componentDidUpdate(prevProps, prevState) {
    // Logout
    if (prevProps.isAuthenticated && !this.props.isAuthenticated) {
      this.cleanupConnections();
      return;
    }

    const authChanged =
      !prevProps.isAuthenticated && this.props.isAuthenticated;

    const locationChanged =
      prevProps.userInfo?.selectedLocationId !==
      this.props.userInfo?.selectedLocationId;

    const mobileChanged = prevState.isMobile !== this.state.isMobile;

    const pathChanged =
      prevProps.location.pathname !== this.props.location.pathname;

    if (authChanged || locationChanged || mobileChanged || pathChanged) {
      this.initConnections();
    }
  }

  componentWillUnmount() {
    this._isUnmounting = true;

    this.cleanupConnections();

    if (this.wakeLock) {
      this.wakeLock.release();
      this.wakeLock = null;
    }

    this.mediaQuery.removeEventListener('change', this.handleScreenChange);
  }

  initConnections = () => {
    const { isAuthenticated, userInfo, location } = this.props;
    const { isMobile } = this.state;

    const isCleanLayout = cleanLayoutPaths.includes(location.pathname);

    if (!isAuthenticated) return;
    if (isCleanLayout) return;
    // Tech admins have ROLE_ADMIN for privilege reasons but still need
    // to receive Maintenance Permit broadcasts, so they bypass the
    // admin early-return and connect the shared WebSocket below.
    const isTechAdmin = this.userIsTechAdmin();

    if (this.userIsAnAdmin() && !isTechAdmin) return;

    if (this.userHasReadingPermission() && !isTechAdmin) return;
    if (!userInfo?.selectedLocationId) return;

    if (isTechAdmin) {
      if (!this.socket) {
        this.connectWebSocket();
      }
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      window.removeEventListener('keydown', this.handleKeyDown);
      this.stopCamera();
      return;
    }

    /* =====================
       📱 MOBILE (RFID ONLY), also connectWebSocket (alert)
       ===================== */
    if (isMobile) {
      if (!this.socketRfid) {
        this.connectRFIDSocket();
      }

      if (!this.socket) {
        this.connectWebSocket();
      }

      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );

      // Ensure desktop-only things are OFF
      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }

      window.removeEventListener('keydown', this.handleKeyDown);
      this.stopCamera();

      return;
    }

    /* =====================
       🖥 DESKTOP (BOTH)
       ===================== */
    if (!this.socket) {
      this.connectWebSocket();
    }

    if (!this.socketRfid) {
      this.connectRFIDSocket();
    }

    this.props.getEmployees({
      locationId: userInfo.selectedLocationId,
    });

    window.removeEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keydown', this.handleKeyDown);

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

    this.startWebcam();
  };

  cleanupConnections = () => {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    if (this.socketRfid) {
      this.socketRfid.close();
      this.socketRfid = null;
    }

    window.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

    this.stopCamera();
  };

  openProfile = () => {
    this.setState({ showProfile: true }, () => {
      this.props.history.push('/profile');
    });
  };

  async requestWakeLock() {
    try {
      if ('wakeLock' in navigator && document.visibilityState === 'visible') {
        this.wakeLock = await navigator.wakeLock.request('screen');

        // Re-acquire when page becomes visible again
        document.addEventListener('visibilitychange', async () => {
          if (document.visibilityState === 'visible' && !this.wakeLock) {
            try {
              this.wakeLock = await navigator.wakeLock.request('screen');
            } catch (err) {
              console.error('Failed to re-request wake lock:', err.message);
            }
          } else if (document.visibilityState !== 'visible' && this.wakeLock) {
            await this.wakeLock.release();
            this.wakeLock = null;
          }
        });
      }
    } catch (err) {
      console.error('Wake Lock error:', err.name, err.message);
    }
  }

  async submitScan(rfid) {
    if (!rfid) return;

    try {
      const callback = (success, message) => {
        // Check if success is false AND the message matches your error string
        if (!success && message?.includes('RFID tag is not registered')) {
          this.setState({
            showSelfRegister: true,
            tempRfid: rfid,
            selectedEmployeeId: null,
          });
        }
        this.setState({ rfid: '' });
      };

      this.props.processRFID({ rfid: rfid }, callback);
    } catch (error) {
      console.error('Error submitting RFID scan:', error);
    }
  }

  handleOnIdle = () => {
    const { userInfo, isAuthenticated } = this.props;
    if (
      isAuthenticated &&
      (userInfo?.authority?.includes(ROLE_ADMIN) ||
        userInfo?.authority?.includes(ROLE_MASTER_ADMIN) ||
        userInfo?.authority?.includes('ROLE_READING'))
    ) {
      AuthUtil.handleLogout();
    }
  };

  handleVisibilityChange = () => {
    if (this.userHasReadingPermission()) return;
    if (document.visibilityState === 'visible') {
      if (
        !this.state.socket ||
        this.state.socket.readyState !== WebSocket.OPEN
      ) {
        this.connectWebSocket();
        // if (!this.state.isMobile) {
        //   this.connectWebSocket();
        // }
      }

      if (
        !this.state.socketRfid ||
        this.state.socketRfid.readyState !== WebSocket.OPEN
      ) {
        this.connectRFIDSocket();
      }
    }
  };

  handleKeyDown = (event) => {
    if (!event?.key) return;
    if (this.timer) clearTimeout(this.timer);

    if (event.key === 'Enter') {
      const tag = this.buffer.trim();
      this.buffer = '';
      if (tag) {
        this.setState({ rfid: tag }, () => this.submitScan(tag));
      }
      return; // Stop execution here
    }

    if (event.key.length === 1) {
      this.buffer += event.key;
    }
    this.timer = setTimeout(() => {
      this.buffer = '';
    }, 500);
  };

  setRefresh = (bool) => {
    this.setState({ triggerUpdate: bool });
  };

  handleScreenChange = (event) => {
    this.setState({ isMobile: event.matches });
  };

  connectRFIDSocket = () => {
    const { selectedLocationId } = this.props.userInfo;
    if (!selectedLocationId) {
      this.props.triggerRefresh(this.state.socketRfid);
      return;
    }

    // Prevent multiple simultaneous sockets
    if (
      this.state.socketRfid &&
      (this.state.socketRfid.readyState === WebSocket.OPEN ||
        this.state.socketRfid.readyState === WebSocket.CONNECTING)
    ) {
      console.log(
        'Socket already open or connecting. Skipping new connection.'
      );
      return;
    }

    const socket = new WebSocket(
      `${process.env.REACT_APP_RFID_WS_URL}?location=${selectedLocationId}`
    );

    let pingTimeout;

    socket.onopen = () => {
      console.log('RFID socket connected.');
      this.setState({ serverStatus: 'UP' });

      // Start keep-alive pings
      this.rfidKeepAlive = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping' }));

          // Wait for pong
          pingTimeout = setTimeout(() => {
            console.warn('No pong received, marking server DOWN.');
            this.setState({ serverStatus: 'DOWN' });
          }, 5000); // 5 sec to wait for pong
        }
      }, 30000); // every 30 sec
    };

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'pong') {
          clearTimeout(pingTimeout);
          if (this.state.serverStatus !== 'UP') {
            this.setState({ serverStatus: 'UP' });
          }
        } else {
          this.handleMessage(message);
        }
      } catch (error) {
        console.error(
          'Error parsing WebSocket message:',
          error,
          'Received:',
          event.data
        );
      }
    };

    socket.onerror = (error) => {
      console.error('RFID WebSocket error:', error);
      this.setState({ serverStatus: 'DOWN' });

      // Only attempt reconnect if socket is not open
      if (!this._reconnectTimeout) {
        this._reconnectTimeout = setTimeout(() => {
          this._reconnectTimeout = null;
          this.connectRFIDSocket();
        }, 2000);
      }
    };

    socket.onclose = (event) => {
      console.log('RFID socket closed.', event.reason);
      clearInterval(this.rfidKeepAlive);
      clearTimeout(pingTimeout);
      this.setState({ serverStatus: 'DOWN' });

      if (!this._isUnmounting && !this._reconnectTimeout) {
        this._reconnectTimeout = setTimeout(() => {
          this._reconnectTimeout = null;
          this.connectRFIDSocket();
        }, 2000);
      }
    };

    this.setState({ socketRfid: socket });
  };

  connectWebSocket = () => {
    console.log('asdsa');
    const { selectedLocationId } = this.props.userInfo;
    if (!selectedLocationId) {
      if (this.state.socket) this.props.triggerRefresh(this.state.socket);
      return;
    }

    // Prevent multiple simultaneous sockets
    if (
      this.state.socket &&
      (this.state.socket.readyState === WebSocket.OPEN ||
        this.state.socket.readyState === WebSocket.CONNECTING)
    ) {
      console.log(
        'WebSocket already open or connecting. Skipping new connection.'
      );
      return;
    }

    // Clean up previous socket
    if (this.state.socket) {
      clearInterval(this.socketKeepAlive);
      this.state.socket.close();
    }

    const clientType = this.userIsTechAdmin()
      ? 'tech-admin'
      : this.state.isMobile
      ? 'alert'
      : 'camera';
    const socket = new WebSocket(
      `${process.env.REACT_APP_WS_URL}?location=${selectedLocationId}&type=${clientType}`
    );

    socket.onopen = () => {
      console.log('WebSocket connected.');

      // Trigger refresh only after connection
      this.props.triggerRefresh(socket);

      // Start keep-alive pings
      this.socketKeepAlive = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000); // every 30 sec
    };

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error(
          'Error parsing WebSocket message:',
          error,
          'Received:',
          event.data
        );
      }
    };

    socket.onclose = () => {
      console.log('WebSocket closed.');
      clearInterval(this.socketKeepAlive);

      if (!this._isUnmounting && !this._wsReconnectTimeout) {
        this._wsReconnectTimeout = setTimeout(() => {
          this._wsReconnectTimeout = null;
          this.connectWebSocket();
        }, 2000);
      }
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      if (!this._wsReconnectTimeout) {
        this._wsReconnectTimeout = setTimeout(() => {
          this._wsReconnectTimeout = null;
          this.connectWebSocket();
        }, 2000);
      }
    };

    this.setState({ socket });
  };

  handleMessage = (message) => {
    const hasCompanyIds =
      this.props.userInfo.companyIds &&
      this.props.userInfo.companyIds.length > 0;

    if (message.action === 'captureImage') {
      if (!hasCompanyIds && !this.state.isMobile) {
        this.captureImage(message.pendingId);
        this.props.triggerRefresh(this.state.socket);
      }
    } else if (message.action === 'black-list-alert') {
      // 1. Set the alert data into state to be passed to children
      this.props.setBlacklistAlert({
        show: true,
        customer: message.customer,
      });
    } else if (message.action === 'refreshData') {
      this.props.triggerRefresh(true);
      this.setRefresh(true);
      setTimeout(() => {
        this.setRefresh(false);
      }, 1000);
    } else if (message.action === 'announcement') {
      this.setState({
        announcementMessage: message.msg,
        announcementEnd: message.datetime,
      });
    } else if (message.action === 'removeAnnouncement') {
      this.setState({ announcementMessage: null, announcementEnd: null });
    } else if (
      message.action === 'permit-granted' ||
      message.action === 'permit-revoked' ||
      message.action === 'permit-expired'
    ) {
      // Maintenance Permit changed at some location. Refresh the
      // company picker so the tech admin (or any NDS staff) sees the
      // location appear/disappear without having to reload. If the
      // location they're currently viewing just lost its permit,
      // kick them back to NDS so they can't keep operating in a site
      // they no longer have access to — clear the selected location
      // in Redux *before* the reload so the company picker doesn't
      // still point at the revoked site after re-hydration.
      if (this.userIsTechAdmin()) {
        this.props.listAvailableCompanies();
        const { userInfo } = this.props;
        const revoked =
          message.action !== 'permit-granted' &&
          message.locationId &&
          userInfo?.selectedLocationId === message.locationId;
        if (revoked) {
          const homeLocationId = userInfo?.company || null;
          this.props.updateUserInfo({
            ...userInfo,
            selectedLocationId: homeLocationId,
            selectedLocationName: homeLocationId ? 'NuVe System' : '',
          });
          setTimeout(() => {
            window.location.reload();
          }, 300);
        }
      }
    }
  };

  startWebcam = async () => {
    if (this.state.isMobile) {
      return;
    }

    // Check if the ref is available
    if (this.videoRef.current) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        // Ensure the videoRef.current is not null before setting srcObject
        if (this.videoRef.current) {
          this.videoRef.current.srcObject = stream;
          this.videoRef.current.play();
        }
      } catch (err) {
        console.error('Error accessing webcam: ', err);
      }
    } else {
      console.error('Video ref is not available');
    }
  };

  sendFileToServer = (pendingId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('pendingId', pendingId);

    fetch(`${BASE_URL}/customer/saveCustomerPointWatching`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: formData,
    }).catch((err) => console.error('Upload error:', err));
  };

  captureImage = (pendingId) => {
    const canvas = this.canvasRef.current;
    const video = this.videoRef.current;
    if (canvas && video) {
      canvas.width = 640;
      canvas.height = 480;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, 640, 480);

      // 0.25 quality — still clear face, ~30-60KB per image
      const compressedImageURL = canvas.toDataURL('image/jpeg', 0.5);
      const uniqueFileName = `${Date.now()}_${Math.floor(
        Math.random() * 10000
      )}.jpg`;
      this.handleFileAdd(compressedImageURL, pendingId, uniqueFileName);
    }
  };

  handleFileAdd = (fileDataURL, pendingId, fileName) => {
    fetch(fileDataURL)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        this.sendFileToServer(pendingId, file);
      });
  };

  protectRoutes = () => {
    const userRoutes = this.props.userInfo.routes
      ? [...this.props.userInfo.routes]
      : [];
    const hasAccess = (route) => {
      if (userRoutes.includes(route.id)) return true;
      if (
        route.requiresAnyOf &&
        route.requiresAnyOf.some((id) => userRoutes.includes(id))
      ) {
        return true;
      }
      return false;
    };
    return getRoutes().filter(hasAccess).concat(routes);
  };

  stopCamera = () => {
    if (this.videoRef.current && this.videoRef.current.srcObject) {
      const stream = this.videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      this.videoRef.current.srcObject = null;
    }
  };

  leftMenuRoutes = () => {
    let displayedRoutes = [];
    const userRoutes = this.props.userInfo.routes
      ? [...this.props.userInfo.routes]
      : [];
    let dividerCount = 0;
    const hasAccess = (route) => {
      if (userRoutes.includes(route.id)) return true;
      if (
        route.requiresAnyOf &&
        route.requiresAnyOf.some((id) => userRoutes.includes(id))
      ) {
        return true;
      }
      return false;
    };
    protectedRoutes.map((route) => {
      if (route.type === ROUTE_TYPE.DIVIDER) {
        if (dividerCount === 0) {
          displayedRoutes = [...displayedRoutes, route];
          dividerCount++;
        }
      } else if (hasAccess(route)) {
        displayedRoutes = [...displayedRoutes, route];
        dividerCount = 0;
      }
      return null;
    });
    return displayedRoutes;
  };

  userIsAnAdmin = () => {
    const { userInfo } = this.props;
    const roles = userInfo?.authority || [];
    return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_MASTER_ADMIN');
  };

  userIsTechAdmin = () => {
    const { userInfo } = this.props;
    const roles = userInfo?.authority || [];
    // Owners have every role baked in, so a raw ROLE_TECH_ADMIN check
    // would treat them as tech admins too. Only treat someone as a
    // real tech admin when they have the role *without* being an owner.
    return roles.includes(ROLE_TECH_ADMIN) && !roles.includes('ROLE_OWNER');
  };

  userHasReadingPermission = () => {
    const { userInfo } = this.props;
    const roles = userInfo?.authority || [];
    return roles.includes('ROLE_READING');
  };

  handleEmployeeChange = (event, newValue) => {
    this.setState({
      selectedEmployeeId: newValue ? newValue.userId : null,
    });
  };

  registerTag = () => {
    const { tempRfid, selectedEmployeeId } = this.state;

    if (!selectedEmployeeId) return;

    this.setState({ isSubmitting: true });

    const data = {
      userId: selectedEmployeeId,
      rfid: tempRfid.trim(),
    };

    const callback = (success, response) => {
      if (success) {
        this.setState({
          showSelfRegister: false,
          tempRfid: '',
          selectedEmployeeId: null,
          isSubmitting: false,
          feedbackOpen: true,
          feedbackMessage: 'RFID Tag successfully linked to employee!',
          feedbackSeverity: 'success',
        });
        this.setRefresh(true);
      } else {
        this.setState({
          isSubmitting: false,
          feedbackOpen: true,
          feedbackMessage:
            response || 'Failed to register tag. Please try again.',
          feedbackSeverity: 'error',
        });
      }
    };

    this.props.registerRFID(data, callback);
  };

  render() {
    const { isMobile } = this.state;
    const { showingMenuItems, isAuthenticated, location } = this.props;

    const isCleanLayout = cleanLayoutPaths.includes(location.pathname);

    const routes = this.leftMenuRoutes().filter((route) => route.id);
    let validPaths = routes.map((route) => route.id);
    if (isMobile) {
      if (validPaths.length !== 1) {
        validPaths = validPaths.filter((id) => id !== 'check-in-out');
      }
    }
    const isValid = validPaths.includes('check-in-out');

    const filteredEmployees = (
      Array.isArray(this.props.employees)
        ? this.props.employees
        : [this.props.employees]
    ).filter((emp) => {
      // 0. Remove disabled employees
      if (emp.isEnabled === false) return false;

      // 1. Exclude employees whose firstname contains "security"
      const isSecurity =
        emp.firstname && emp.firstname.toLowerCase().includes('security');

      if (isSecurity) return false;

      // 2 Exclude ROLE_ADMIN (and MASTER_ADMIN if needed)
      const hasAdminRole =
        Array.isArray(emp.authorities) &&
        emp.authorities.some(
          (a) =>
            a.authority === 'ROLE_ADMIN' || a.authority === 'ROLE_MASTER_ADMIN'
        );

      if (hasAdminRole) return false;

      // 3. Show only employees WITHOUT RFID
      const hasRfid = emp.rfid != null && String(emp.rfid).trim() !== '';

      return !hasRfid;
    });

    return (
      <>
        <div className="main-layout-wrapper">
          <div className="custom-container-fluid content-grow">
            <div style={{ width: '100%' }}>
              {isAuthenticated && !isCleanLayout && (
                <>
                  <Header
                    logout={this.props.authLogout}
                    firstname={this.props.userInfo.firstname}
                    userRoutes={[...this.props.userInfo.routes]}
                    routes={this.leftMenuRoutes()}
                    refresh={this.state.triggerUpdate}
                    setRefresh={this.setRefresh}
                    isAuthenticated={this.props.isAuthenticated}
                    announcementMessage={this.state.announcementMessage}
                    announcementEnd={this.state.announcementEnd}
                    serverStatus={this.state.serverStatus}
                    openProfile={this.openProfile}
                  />
                  <IdleTimer
                    ref={(ref) => (this.idleTimer = ref)}
                    timeout={60 * 60 * 1000} // 1 hour
                    onIdle={this.handleOnIdle}
                    debounce={500}
                  />
                </>
              )}
              {this.props.isAuthenticated && (
                <input
                  type="text"
                  ref={this.inputRef}
                  onKeyDown={this.handleKeyDown}
                  onBlur={() => this.inputRef.current?.focus()}
                  style={{ opacity: 0, position: 'absolute' }}
                />
              )}

              <div className="custom-container-fluid">
                {!isCleanLayout && (
                  <div
                    className="leftContainer"
                    style={{ backgroundColor: '#000c36', color: 'white' }}
                  >
                    {this.props.isAuthenticated &&
                      window.screen.width > 800 && (
                        <Slide
                          direction="right"
                          in={this.props.showingMenuItems}
                          mountOnEnter
                          unmountOnExit
                        >
                          <LeftMenu
                            logout={this.props.authLogout}
                            routes={this.leftMenuRoutes()}
                            userRoutes={[...this.props.userInfo.routes]}
                            isDrawer={false}
                          />
                        </Slide>
                      )}
                  </div>
                )}

                <div
                  className={
                    showingMenuItems &&
                    !isMobile &&
                    isAuthenticated &&
                    !isCleanLayout
                      ? 'rightContainer'
                      : 'fullWidthContainer'
                  }
                >
                  <Switch>
                    {this.protectRoutes().map((route) => (
                      <Route
                        key={route.id}
                        component={route.component}
                        path={route.path}
                      />
                    ))}

                    <Route exact path="/">
                      <Redirect
                        to={isAuthenticated ? '/dashboard' : '/login'}
                      />
                    </Route>

                    {/* Catch-all for any other unmatched routes */}
                    <Route path="*">
                      {isAuthenticated ? (
                        isMobile ? (
                          isValid ? (
                            <CheckIn404 />
                          ) : this.props.userInfo.sessionId ? (
                            <PageNotFound />
                          ) : (
                            <CheckIn404 />
                          )
                        ) : isValid ? (
                          <ClockInOut />
                        ) : this.props.userInfo.sessionId ? (
                          <PageNotFound />
                        ) : (
                          <CheckIn404 />
                        )
                      ) : (
                        <Login />
                      )}
                    </Route>
                  </Switch>
                </div>
              </div>
            </div>
          </div>

          <Footer />
          <video ref={this.videoRef} style={{ display: 'none' }} />
          <canvas ref={this.canvasRef} style={{ display: 'none' }} />
        </div>

        <Snackbar
          open={this.state.feedbackOpen}
          autoHideDuration={5000}
          onClose={() => this.setState({ feedbackOpen: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => this.setState({ feedbackOpen: false })}
            severity={this.state.feedbackSeverity}
            variant="filled"
            elevation={6}
          >
            {this.state.feedbackMessage}
          </Alert>
        </Snackbar>

        {this.props.blacklistAlert?.show && (
          <>
            <Dialog
              fullScreen={this.state.isMobile}
              open={true}
              maxWidth="md"
              fullWidth
              PaperProps={{
                style: {
                  borderRadius: this.state.isMobile ? 0 : 12,
                  overflow: 'hidden',
                },
              }}
            >
              {/* --- SHARED HEADER --- */}
              <div
                style={{
                  backgroundColor: '#b71c1c',
                  color: 'white',
                  padding: this.state.isMobile ? '16px' : '24px',
                  textAlign: 'center',
                  borderBottom: '6px solid #801313',
                  textTransform: 'uppercase',
                }}
              >
                <h1
                  style={{
                    margin: 0,
                    fontSize: this.state.isMobile ? '1.5rem' : '2.2rem',
                    fontWeight: 900,
                  }}
                >
                  <span
                    role="img"
                    aria-label="siren"
                    style={{ marginRight: '10px' }}
                  >
                    🚨
                  </span>
                  Blacklist Alert
                </h1>
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: this.state.isMobile ? '0.9rem' : '1.2rem',
                    fontWeight: 500,
                    opacity: 0.9,
                  }}
                >
                  Access Denied - Do Not Allow Entry
                </p>
              </div>

              <DialogContent
                style={{ padding: this.state.isMobile ? 0 : '32px' }}
              >
                {this.state.isMobile ? (
                  /* --- MOBILE LAYOUT (Stacked) --- */
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div onClick={() => this.setState({ isImageZoomed: true })}>
                      <img
                        src={this.props.blacklistAlert.customer.files?.[0]?.localURL?.replace(
                          /\\/g,
                          '/'
                        )}
                        style={{
                          width: '100%',
                          height: '45vh',
                          objectFit: 'cover',
                        }}
                        alt="Customer"
                      />
                    </div>
                    <div
                      style={{
                        marginLeft: '16px',
                        marginRight: '16px',
                        marginTop: '16px',
                        textAlign: 'left',
                      }}
                    >
                      <Typography
                        variant="overline"
                        style={{ color: '#d32f2f', fontWeight: 'bold' }}
                      >
                        CUSTOMER IDENTITY
                      </Typography>
                      <Typography
                        variant="h4"
                        style={{
                          fontWeight: 900,
                          color: '#222',
                          textTransform: 'uppercase',
                        }}
                      >
                        {this.props.blacklistAlert.customer.firstName}{' '}
                        {this.props.blacklistAlert.customer.lastName}
                      </Typography>
                      <Typography
                        variant="body1"
                        style={{ color: '#666', marginBottom: '20px' }}
                      >
                        {this.props.blacklistAlert.customer.phone}
                      </Typography>
                    </div>
                    <div
                      style={{
                        marginLeft: '16px',
                        marginRight: '16px',
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: '#fff5f5',
                          border: '2px dashed #d32f2f',
                          padding: '16px',
                          borderRadius: '8px',
                          marginBottom: '24px',
                        }}
                      >
                        <Typography
                          variant="caption"
                          style={{
                            color: '#d32f2f',
                            fontWeight: 'bold',
                            display: 'block',
                          }}
                        >
                          REASON
                        </Typography>
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 'bold' }}
                        >
                          "{this.props.blacklistAlert.customer.blockReason}"
                        </Typography>
                      </div>

                      <Button
                        fullWidth
                        variant="contained"
                        style={{
                          backgroundColor: '#000',
                          color: '#fff',
                          padding: '16px',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          marginBottom: '24px',
                        }}
                        onClick={() =>
                          this.props.setBlacklistAlert({
                            show: false,
                            customer: null,
                          })
                        }
                      >
                        ACKNOWLEDGE & DISMISS ALERT
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* --- DESKTOP LAYOUT (Side-by-Side) --- */
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={6}>
                      <div
                        style={{ cursor: 'zoom-in', position: 'relative' }}
                        onClick={() => this.setState({ isImageZoomed: true })}
                      >
                        <img
                          src={this.props.blacklistAlert.customer.files?.[0]?.localURL?.replace(
                            /\\/g,
                            '/'
                          )}
                          style={{
                            width: '100%',
                            height: '420px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                          }}
                          alt="Customer"
                        />
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 12,
                            right: 12,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                          }}
                        >
                          CLICK TO ENLARGE
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="overline"
                        style={{ color: '#d32f2f', fontWeight: 'bold' }}
                      >
                        CUSTOMER IDENTITY
                      </Typography>
                      <Typography
                        variant="h3"
                        style={{
                          fontWeight: 900,
                          color: '#222',
                          marginBottom: '8px',
                          lineHeight: 1.1,
                          textTransform: 'uppercase',
                        }}
                      >
                        {this.props.blacklistAlert.customer.firstName}{' '}
                        {this.props.blacklistAlert.customer.lastName}
                      </Typography>
                      <Typography
                        variant="h6"
                        style={{ color: '#666', marginBottom: '24px' }}
                      >
                        {this.props.blacklistAlert.customer.phone}
                      </Typography>

                      <div
                        style={{
                          backgroundColor: '#fff5f5',
                          borderLeft: '6px solid #d32f2f',
                          padding: '20px',
                          borderRadius: '4px',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          style={{ color: '#d32f2f', fontWeight: 'bold' }}
                        >
                          RESTRICTION REASON
                        </Typography>
                        <Typography
                          variant="body1"
                          style={{ fontStyle: 'italic', fontSize: '1.1rem' }}
                        >
                          "{this.props.blacklistAlert.customer.blockReason}"
                        </Typography>
                      </div>

                      <Button
                        fullWidth
                        variant="contained"
                        style={{
                          marginTop: '32px',
                          backgroundColor: '#000',
                          color: '#fff',
                          padding: '18px',
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                        }}
                        onClick={() =>
                          this.props.setBlacklistAlert({
                            show: false,
                            customer: null,
                          })
                        }
                      >
                        ACKNOWLEDGE & DISMISS ALERT
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
            </Dialog>

            {/* --- SHARED ZOOM DIALOG --- */}
            <Dialog
              open={this.state.isImageZoomed}
              onClose={() => this.setState({ isImageZoomed: false })}
              maxWidth="lg"
              PaperProps={{
                style: { backgroundColor: 'transparent', boxShadow: 'none' },
              }}
            >
              <div
                style={{ textAlign: 'center' }}
                onClick={() => this.setState({ isImageZoomed: false })}
              >
                <img
                  src={this.props.blacklistAlert.customer.files?.[0]?.localURL?.replace(
                    /\\/g,
                    '/'
                  )}
                  style={{
                    maxWidth: '95vw',
                    maxHeight: '90vh',
                    border: '2px solid white',
                    borderRadius: '4px',
                  }}
                  alt="Zoomed"
                />
                <p style={{ color: 'white', marginTop: '10px' }}>
                  Click anywhere to close
                </p>
              </div>
            </Dialog>
          </>
        )}

        {this.state.showSelfRegister && (
          <Dialog
            open={this.state.showSelfRegister}
            onClose={() => this.setState({ showSelfRegister: false })}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              style: { borderRadius: 12, padding: '8px' },
            }}
          >
            <DialogTitle
              style={{ textAlign: 'center', fontWeight: 600, color: '#1a237e' }}
            >
              ASSIGN UNREGISTERED TAG
            </DialogTitle>

            <DialogContent>
              <Box
                mb={3}
                p={2}
                style={{
                  backgroundColor: '#f5f7fb',
                  borderRadius: 8,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  gutterBottom
                >
                  DETECTED NEW TAG
                </Typography>
                <Typography
                  variant="h6"
                  style={{
                    letterSpacing: '2px',
                    fontWeight: 'bold',
                    color: '#333',
                  }}
                >
                  {/* SECURITY MASKING: Shows first 2 and last 2 digits only */}
                  {this.state.tempRfid.length > 4
                    ? `${this.state.tempRfid.substring(
                        0,
                        2
                      )}****${this.state.tempRfid.slice(-2)}`
                    : '****'}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: '16px' }}
              >
                This tag isn't recognized. Select the employee who will be using
                this RFID for access.
              </Typography>

              <Autocomplete
                options={filteredEmployees} // Only show users without tags
                getOptionLabel={(option) =>
                  `${option.firstname || ''} ${option.lastname || ''}`.trim() ||
                  option.username
                }
                onChange={this.handleEmployeeChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Employee"
                    variant="outlined"
                    placeholder="Type name..."
                    fullWidth
                  />
                )}
              />
            </DialogContent>

            <DialogActions style={{ padding: '16px 24px' }}>
              <Button
                onClick={() => this.setState({ showSelfRegister: false })}
                style={{ color: '#757575', textTransform: 'none' }}
              >
                Dismiss
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!this.state.selectedEmployeeId}
                onClick={this.registerTag}
                style={{
                  backgroundColor: '#1a237e',
                  textTransform: 'none',
                  padding: '8px 24px',
                  borderRadius: '6px',
                  fontWeight: 600,
                }}
              >
                Complete Registration
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  }
}

Base.propTypes = {
  socket: PropTypes.object,
  userInfo: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  showingMenuItems: PropTypes.bool,
};

Base.defaultProps = {
  socket: null,
  userInfo: {},
  isAuthenticated: false,
  showingMenuItems: false,
};

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  isAuthenticated: isAuthenticatedSelector(state),
  showingMenuItems: showingMenuItemsSelector(state),
  employees: employeesSelector(state),
  blacklistAlert: blacklistAlertSelector(state),
});

const actionCreators = {
  authLogout,
  triggerRefresh,
  setBlacklistAlert,
  processRFID,
  getEmployees,
  registerRFID,
  listAvailableCompanies,
  updateUserInfo,
};

export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  withRouter(Base)
);
